const dataAccessMethods = require('../../../dataAccessMethods');
const Blockly = require('blockly');
const DelegatingMdcBlockGroup = require('../_DelegatingMdcBlockGroup');
const { config } = require('@sstdev/lib_metadata-config');
const setupAddCodeProperties = require('../../../helpers/addCodeProperties');
const convertToBoolean = require('../../../helpers/convertToBoolean');
const arrayFromCommaSeparatedValues = require('../../../helpers/arrayFromCommaSeparatedValues');
const logging = require('@sstdev/lib_logging');

const TOOLTIPS = {
    FindRecord:
        'Lookup record by a single property,(possibly always on the server). Select it for edit if found, prepare new record if not found. (E.g. VIN lookup).',
    MarkRecord:
        'Find a record by one or more properties. Custom Middleware sets values from Search Elements (e.g. Location) when found or creates a new record if not found (FLAIR/AT "Take").',
    KeySearch: 'Search field to filter a grid on one or more properties, as characters are typed.',
    DateRangePicker: 'OBSOLETE: Use `Range` Action Element instead.',
    DeferredDynamicFieldEntry:
        'Allows creation or update **of an operation** that will be stored on some record, and which will be used LATER to update the given namespace/relation. Useful for creating workflows which update a field of some namespace/relation when some future action happens.',
    DynamicFieldEntry:
        'Allows creating a transactional operation where a user can select and overwrite any field already associated (elsewhere in this useCase) with the given namespace/relation.  Not useful for edit/update forms that store update operations for later - see DeferredDynamicFieldEntry instead.'
};

const hNodeTypes = require('./types');

class FormElement extends DelegatingMdcBlockGroup {
    constructor(hNodeTypeGroup, categorySpec, color, subset = '') {
        super(hNodeTypeGroup, categorySpec, color, subset);
        super.types = hNodeTypes;
    }

    /**
     * @param {{[key: string]: any}} code
     * @param {import('blockly').Block} blocklyBlock
     * @param {string} hNodeType
     */
    addCodeGeneratorCustom(code, blocklyBlock, hNodeType) {
        if (this.types[hNodeType]?.convertCustomFieldsToCode) {
            const resultCode = this.types[hNodeType].convertCustomFieldsToCode(code, blocklyBlock);
            if (typeof resultCode === 'string') {
                logging.error(
                    `${hNodeType}.convertCustomFieldsToCode should not return a string. It should return a JSON object instead.`
                );
                return resultCode;
            }
            return JSON.stringify(resultCode);
        }

        const addCodeProperties = setupAddCodeProperties(code, blocklyBlock);

        if (!['DynamicFieldEntry'].includes(hNodeType)) {
            addCodeProperties([
                'propertyName',
                {
                    key: 'sequence',
                    // Song and dance to figure out the sequence based on order in the workspace.
                    handler: () => {
                        const parent = blocklyBlock.getSurroundParent();
                        // if the block is not docked, it won't have a parent.
                        // Therefore, our sequence # is by definition 0:
                        if (!parent) return 0;
                        const parentChildren = parent.getDescendants(false);
                        return parentChildren.findIndex(value => value.id === blocklyBlock.id) - 1; // subtract 1 because includes parent with getDescendants
                    }
                }
            ]);
        }

        if (hNodeType === 'ReadOnlyText') {
            addCodeProperties([{ key: 'compact', handler: convertToBoolean }, 'busyProperty']);
        }
        if (hNodeType === 'FormCardList') {
            addCodeProperties([{ key: 'selectionProperty', handler: value => value || undefined }]);
        }
        if (hNodeType === 'ReadOnlyFormList') {
            // Carry MDC's row filtering and checkbox settings into the UI metadata.
            addCodeProperties([
                { key: 'selectionProperty', handler: value => value || undefined },
                'selectableProperty',
                'selectableValue',
                'selectionEnabledProperty',
                'selectionEnabledValue',
                'rowFilterProperty',
                'rowFilterDefaultValue'
            ]);
            // MDC accepts key names as text; the UI needs an array of those names.
            addCodeProperties([{ key: 'selectionKeys', handler: arrayFromCommaSeparatedValues }]);
            addCodeProperties(
                ['rowFilterValues', 'rowFilterLabels'].map(key => ({ key, handler: arrayFromCommaSeparatedValues }))
            );
        }

        if (hNodeType === 'DatePicker') {
            addCodeProperties(['format']);
            addCodeProperties([{ key: 'showAsModal', handler: convertToBoolean }]);
        }

        if (hNodeType === 'Coordinates') {
            addCodeProperties(['firstCoordinateLabel', 'secondCoordinateLabel']);
        }

        // Exclude boolean and readonly types from having a required flag
        if (!['Toggle', 'ReadOnlyDateTime', 'ReadOnlyFormList', 'FormCardList', 'ReadOnlyText', 'JsonViewer'].includes(hNodeType)) {
            addCodeProperties([{ key: 'required', handler: convertToBoolean }]);
        }

        if (hNodeType === 'DropDown') {
            addCodeProperties([{ key: 'optionsProperty', handler: value => value || undefined }]);
        }

        if (hNodeType.includes('DropDown')) {
            addCodeProperties([
                { key: 'displayUnassignedRow', handler: convertToBoolean },
                { key: 'dropdownDisplayProperties', handler: arrayFromCommaSeparatedValues },
                { key: 'inputDisplayProperties', handler: arrayFromCommaSeparatedValues },
                { key: 'autoHide', handler: convertToBoolean },
                {
                    key: 'propertyOverride',
                    // Dropdowns normally store selections under namespace:relation. Use this only when
                    // the lookup relation is generic but the saved field has domain meaning to rules/API code.
                    handler: value => value || undefined
                },
                'placeholder',
                'emptyHint'
            ]);
        }
        if (
            hNodeType.includes('DropDown') ||
            ['Image', 'DynamicFieldEntry', 'DeferredDynamicFieldEntry'].includes(hNodeType)
        ) {
            addCodeProperties([
                { key: 'foreignNamespace', value: 'namespace' },
                { key: 'foreignRelation', value: 'relation' }
            ]);
        }

        if (['Integer', 'Currency', 'Slider'].includes(hNodeType)) {
            addCodeProperties([
                { key: 'max', handler: parseInt },
                { key: 'min', handler: parseInt },
                { key: 'defaultValue', handler: parseInt }
            ]);
        }
        if (hNodeType === 'Slider') {
            addCodeProperties(['minIconName', 'maxIconName']);
        }

        if (hNodeType === 'LongText') {
            addCodeProperties(['rows']);
        }

        if (['ShortText', 'MultiPickerShortText'].includes(hNodeType)) {
            addCodeProperties([
                {
                    key: 'pattern',
                    value: 'validationPattern',
                    handler: value => {
                        if (value) return value;
                    }
                }
            ]);
            // @ts-ignore
            code.transforms = JSON.parse(`[${Blockly.JavaScript.statementToCode(blocklyBlock, 'transforms')}]`);
        }

        if (hNodeType === 'MultiPickerSensorGrid') {
            addCodeProperties([
                { key: 'mergeNamespace', value: 'namespace' },
                { key: 'mergeRelation', value: 'relation' },
                { key: 'matchProperties', handler: arrayFromCommaSeparatedValues },
                'textForShowUnknownTagsCheckbox',
                'rowClickAction',
                { key: 'namespace', handler: () => 'sensor' },
                { key: 'relation', handler: () => 'read' }
            ]);
        }

        if (['ShortEncodedText', 'ShortText', 'LongText'].includes(hNodeType)) {
            addCodeProperties([
                { key: 'minLength', handler: parseInt },
                { key: 'maxLength', handler: parseInt },
                'defaultValue'
            ]);
        }

        if (['MultiPickerShortText'].includes(hNodeType)) {
            addCodeProperties([
                { key: 'minLength', handler: parseInt },
                { key: 'maxLength', handler: parseInt },
                {
                    key: 'defaultValue',
                    handler: value => {
                        try {
                            const parsed = JSON.parse(value);
                            if (Array.isArray(parsed)) return parsed;
                        } catch (error) {
                            /* do nothing */
                        }
                    }
                }
            ]);
        }

        if (['DeferredDynamicFieldEntry', 'DynamicFieldEntry'].includes(hNodeType)) {
            addCodeProperties([{ key: 'excludedFields', handler: arrayFromCommaSeparatedValues }]);
        }

        // ** This isn't needed right now, but I can't see us going too long without needing it. **
        // It would be the ability to configure who an update, and who can remove EditableList entries
        // if (hNodeType === 'EditableList') {
        //     let canUpdate = Blockly.JavaScript.statementToCode(blocklyBlock, 'canUpdate');
        //     if (canUpdate) {
        //         code.rowUpdateFilters = JSON.parse(`[${canUpdate}]`);
        //     }
        //     let canRemove = Blockly.JavaScript.statementToCode(blocklyBlock, 'canRemove');
        //     if (canRemove) {
        //         code.rowRemoveFilters = JSON.parse(`[${canRemove}]`);
        //     }
        // }

        if (
            hNodeType.includes('DropDown') ||
            ![
                'DeferredDynamicFieldEntry',
                'DynamicFieldEntry',
                'EditableList',
                'ReadOnlyDateTime',
                'ReadOnlyFormList',
                'FormCardList',
                'ReadOnlyText',
                'Email',
                'PasswordWithConfirm',
                'DropDown',
                'MultiPickerDropDown',
                'JsonViewer',
                'MultiPickerSensorGrid'
            ].includes(hNodeType)
        ) {
            addCodeProperties(['tooltip']);
        }

        if (
            ![
                'DynamicFieldEntry',
                'DeferredDynamicFieldEntry',
                'EditableList',
                'Image',
                'Password',
                'PasswordWithConfirm',
                'JsonViewer',
                'MultiPickerSensorGrid'
            ].includes(hNodeType)
        ) {
            addCodeProperties([{ key: 'excludeFromImport', handler: convertToBoolean }]);
        }

        if (
            ![
                'Coordinates',
                'Currency',
                'InvisibleElement',
                'JsonViewer',
                'DatePicker',
                'EditableList',
                'Toggle',
                'ReadOnlyDateTime',
                'ReadOnlyFormList',
                'FormCardList',
                'ReadOnlyText',
                'Slider',
                'Password',
                'PasswordWithConfirm',
                'Image'
            ].includes(hNodeType)
        ) {
            addCodeProperties([
                { key: 'displayScanButton', handler: convertToBoolean },
                { key: 'displayRfidPowerButton', handler: convertToBoolean },
                { key: 'scanRfid', handler: convertToBoolean },
                { key: 'scanBarcode', handler: convertToBoolean }
            ]);
        }

        if (hNodeType === 'EditableList') {
            addCodeProperties([{ key: 'retainParentContext', handler: convertToBoolean }]);
        }

        return JSON.stringify(code);
    }

    /**
     *
     * @param {unknown} color - the configured color for this block
     * @param {import('blockly').Block} blocklyBlock - the blockly block (as defined in the Blockly documentation https://developers.google.com/blockly/reference/js/Blockly.Block)
     * @param {string} hNodeType,
     * @param {object} mdcBlock - configuration for this block
     * @param {object} topInput - the top (default) input already added to this block
     */
    addToBlocksCustom(color, blocklyBlock, hNodeType, mdcBlock, topInput) {
        if (this.types[hNodeType]?.addBlockSpecificFields) {
            this.types[hNodeType].addBlockSpecificFields(color, blocklyBlock, mdcBlock, topInput);
            return;
        }

        if (TOOLTIPS[hNodeType]) {
            blocklyBlock.setTooltip(TOOLTIPS[hNodeType]);
        }
        const blockId = blocklyBlock.id;
        const workspace = blocklyBlock.workspace;

        if (['DeferredDynamicFieldEntry', 'DynamicFieldEntry'].includes(hNodeType)) {
            topInput.removeField('titleLabel');
            topInput.removeField('title');
        }

        if (hNodeType === 'ReadOnlyText') {
            topInput.appendField('  Compact:').appendField(new Blockly.FieldCheckbox(false), 'compact')
                .appendField('  Busy form field (optional):').appendField(new Blockly.FieldTextInput(), 'busyProperty');
        }

        if (hNodeType === 'Coordinates') {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  1st Coordinate Label:')
                .appendField(new Blockly.FieldTextInput('Latitude'), 'firstCoordinateLabel')
                .appendField('  2nd Coordinate Label:')
                .appendField(new Blockly.FieldTextInput('Longitude'), 'secondCoordinateLabel');
        }

        if (
            hNodeType.includes('DropDown') ||
            ['DeferredDynamicFieldEntry', 'DynamicFieldEntry', 'Image'].includes(hNodeType)
        ) {
            let namespaceDropDown = new Blockly.FieldDropdown(dataAccessMethods.getNamespaces);
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Namespace:')
                .appendField(namespaceDropDown, 'namespace');
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Relation:')
                .appendField(
                    new Blockly.FieldDropdown(() => dataAccessMethods.getRelations(workspace, blockId)),
                    'relation'
                );
            if (hNodeType === 'DropDown') {
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Options from form property:')
                    .appendField(new Blockly.FieldTextInput(''), 'optionsProperty');
            }
            if (hNodeType.includes('DropDown')) {
                blocklyBlock
                    .appendDummyInput('propertyName')
                    .appendField(new Blockly.FieldTextInput('title'), 'propertyName')
                    .setVisible(false);
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Property Override:')
                    .appendField(new Blockly.FieldTextInput(''), 'propertyOverride');
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Auto Hide:')
                    .appendField(new Blockly.FieldCheckbox('false'), 'autoHide');
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Tooltip:')
                    .appendField(new Blockly.FieldTextInput(''), 'tooltip');
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Properties to include in dropdown:')
                    .appendField(new Blockly.FieldTextInput('title'), 'dropdownDisplayProperties');
                blocklyBlock
                    .appendDummyInput()
                    .setAlign(Blockly.ALIGN_RIGHT)
                    .appendField('  Properties to include in input:')
                    .appendField(new Blockly.FieldTextInput('title'), 'inputDisplayProperties');
                blocklyBlock.appendStatementInput('filters').setCheck(['filter']).appendField('Filters:');
            } else {
                if (!['DynamicFieldEntry'].includes(hNodeType)) {
                    blocklyBlock
                        .appendDummyInput()
                        .setAlign(Blockly.ALIGN_RIGHT)
                        .appendField('  Property Name:')
                        .appendField(new Blockly.FieldTextInput('title'), 'propertyName');
                }
            }
        } else {
            topInput.appendField('  Property Name:').appendField(new Blockly.FieldTextInput(''), 'propertyName');
        }

        if (hNodeType.includes('DropDown')) {
            blocklyBlock
                .appendDummyInput()
                .appendField('  Display Unassigned Row:')
                .appendField(new Blockly.FieldCheckbox(true), 'displayUnassignedRow')
                .appendField('  Placeholder')
                .appendField(new Blockly.FieldTextInput(), 'placeholder')
                .appendField('  Empty Hint')
                .appendField(new Blockly.FieldTextInput('No records found'), 'emptyHint');
        }
        if (['Integer', 'Currency', 'Slider'].includes(hNodeType)) {
            const isSlider = hNodeType === 'Slider';
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Min:')
                .appendField(new Blockly.FieldNumber('0', Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, 1), 'min');
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Max:')
                .appendField(
                    new Blockly.FieldNumber(
                        isSlider ? '100' : '9999',
                        Number.MIN_SAFE_INTEGER,
                        Number.MAX_SAFE_INTEGER,
                        1
                    ),
                    'max'
                );
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Default Value:')
                .appendField(
                    new Blockly.FieldNumber(
                        isSlider ? '100' : '0',
                        Number.MIN_SAFE_INTEGER,
                        Number.MAX_SAFE_INTEGER,
                        1
                    ),
                    'defaultValue'
                );
        }

        if (hNodeType === 'LongText') {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Rows:')
                .appendField(
                    new Blockly.FieldNumber('20', Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, 1),
                    'rows'
                );
        }

        /**
         * if the required field is checked and this is a text type block,
         * make the minLength the greater of an existing minLength and 1
         * @param {boolean} isChecked if the new value is true
         */
        function minOneIfRequired(isChecked) {
            if (
                isChecked &&
                ['ShortEncodedText', 'ShortText', 'LongText', 'MultiPickerShortText'].includes(hNodeType)
            ) {
                let field = blocklyBlock.getField('minLength');
                field.setValue(Math.max(field.getValue(), 1));
            }
            return isChecked;
        }
        // Exclude boolean and readonly types from having a required flag
        if (
            ![
                'DeferredDynamicFieldEntry',
                'DynamicFieldEntry',
                'Toggle',
                'ReadOnlyDateTime',
                'ReadOnlyFormList',
                'FormCardList',
                'ReadOnlyText',
                'MultiPickerSensorGrid'
            ].includes(hNodeType)
        ) {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Required:')
                .appendField(new Blockly.FieldCheckbox('false', minOneIfRequired), 'required');
        }

        if (['ShortEncodedText', 'ShortText', 'LongText', 'MultiPickerShortText'].includes(hNodeType)) {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Min Length:')
                .appendField(
                    new Blockly.FieldNumber('0', Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, 1),
                    'minLength'
                );
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Max Length:')
                .appendField(
                    new Blockly.FieldNumber('100', Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, 1),
                    'maxLength'
                );

            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Default Value:')
                .appendField(new Blockly.FieldTextInput(''), 'defaultValue');
        }

        if (['ShortText', 'MultiPickerShortText'].includes(hNodeType)) {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Validation Pattern:')
                .appendField(new Blockly.FieldTextInput(''), 'validationPattern');
        }

        if (hNodeType === 'EditableList') {
            // Allow form elements inside an editable list.
            blocklyBlock
                .appendDummyInput()
                .appendField('  Retains context from parent')
                .appendField(new Blockly.FieldCheckbox('false'), 'retainParentContext');

            blocklyBlock
                .appendStatementInput('editableListElements')
                .setCheck(['formElement'])
                .appendField('Form Elements:');
            // ** This isn't needed right now, but I can't see us going too long without needing it. **
            // blocklyBlock.appendDummyInput().appendField('Row Action Filters:');
            // blocklyBlock.appendStatementInput('canUpdate').setCheck(['filter']).appendField(' Can update:').setAlign(Blockly.ALIGN_RIGHT);
            // blocklyBlock.appendStatementInput('canRemove').setCheck(['filter']).appendField(' Can remove:').setAlign(Blockly.ALIGN_RIGHT);
        }

        if (['ReadOnlyFormList', 'FormCardList'].includes(hNodeType)) {
            const selectionFields = hNodeType === 'ReadOnlyFormList' ? [
                ['selectionProperty', 'Save selected rows to (optional):'],
                ['selectionKeys', 'Selected key properties (comma-separated; empty saves whole rows):'],
                ['selectableProperty', 'Selectable row property (optional):'],
                ['selectableValue', 'Selectable row value:'],
                ['selectionEnabledProperty', 'Selection enabled property (optional):'],
                ['selectionEnabledValue', 'Selection enabled value:'],
                ['rowFilterProperty', 'Filter rows by property (optional):'],
                ['rowFilterValues', 'Filter values (comma-separated; All is included):'],
                ['rowFilterLabels', 'Filter labels (comma-separated, in the same order):'],
                ['rowFilterDefaultValue', 'Default filter value (empty shows All):']
            ] : [['selectionProperty', 'Save selected rows to (optional):']];
            for (const [key, label] of selectionFields) {
                blocklyBlock
                    .appendDummyInput()
                    .appendField(label)
                    .appendField(new Blockly.FieldTextInput(''), key);
            }
            blocklyBlock
                .appendStatementInput('formListElements')
                .setCheck(['listColumn'])
                .appendField('Columns:');
        }

        if (hNodeType === 'MultiPickerSensorGrid') {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Merges scanned value with')
                .appendField(new Blockly.FieldTextInput('tagId'), 'matchProperties')
                .appendField(' from namespace:')
                .appendField(new Blockly.FieldDropdown(dataAccessMethods.getNamespaces), 'namespace')
                .appendField(' and relation:')
                .appendField(
                    new Blockly.FieldDropdown(() => dataAccessMethods.getRelations(workspace, blockId)),
                    'relation'
                );
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Text for "Show Unknown Tags" Checkbox: ')
                .appendField(new Blockly.FieldTextInput('Show Unknown Tags'), 'textForShowUnknownTagsCheckbox');

            const rowClickDropDown = new Blockly.FieldDropdown(config.actionElementActions);
            rowClickDropDown.setValue('edit');
            topInput.appendField('  Row Click Action:').appendField(rowClickDropDown, 'rowClickAction');

            blocklyBlock.setInputsInline(false);
            blocklyBlock.appendStatementInput('columns').setCheck(['listColumn']).appendField('List Columns:');
        }

        if (
            !hNodeType.includes('DropDown') &&
            ![
                'DeferredDynamicFieldEntry',
                'DynamicFieldEntry',
                'EditableList',
                'ReadOnlyDateTime',
                'ReadOnlyFormList',
                'FormCardList',
                'ReadOnlyText',
                'Email',
                'PasswordWithConfirm',
                'DropDown',
                'MultiPickerDropDown',
                'JsonViewer',
                'MultiPickerSensorGrid'
            ].includes(hNodeType)
        ) {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Tooltip:')
                .appendField(new Blockly.FieldTextInput(''), 'tooltip');
        }
        if (
            ![
                'DeferredDynamicFieldEntry',
                'DynamicFieldEntry',
                'EditableList',
                'Image',
                'Password',
                'PasswordWithConfirm',
                'JsonViewer',
                'MultiPickerSensorGrid',
                'ReadOnlyFormList',
                'FormCardList',
                'ReadOnlyText'
            ].includes(hNodeType)
        ) {
            blocklyBlock
                .appendDummyInput()
                .appendField('  Exclude from Import:')
                .appendField(new Blockly.FieldCheckbox(), 'excludeFromImport');
        }
        if (hNodeType === 'ShortText') {
            blocklyBlock.appendStatementInput('transforms').setCheck(['transform']).appendField('Transforms:');
        }

        if (hNodeType === 'ShortEncodedText') {
            blocklyBlock
                .appendStatementInput('sensorReadButton')
                .setCheck(['actionElement'])
                .appendField('(optional) Sensor Read Button: ');
        }

        if (hNodeType === 'DatePicker') {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Display Format:')
                .appendField(new Blockly.FieldDropdown(config.dateTimeFormatOptions), 'format')
                .appendField('  Show as modal:')
                .appendField(new Blockly.FieldCheckbox(), 'showAsModal');
        }

        if (['DeferredDynamicFieldEntry', 'DynamicFieldEntry'].includes(hNodeType)) {
            blocklyBlock
                .appendDummyInput()
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Fields to Exclude:')
                .appendField(new Blockly.FieldTextInput('title,assetNo,tagId'), 'excludedFields');
        }
        if (
            ![
                'Coordinates',
                'Currency',
                'InvisibleElement',
                'JsonViewer',
                'DatePicker',
                'EditableList',
                'Toggle',
                'ReadOnlyDateTime',
                'ReadOnlyFormList',
                'FormCardList',
                'ReadOnlyText',
                'Slider',
                'Password',
                'PasswordWithConfirm',
                'Image'
            ].includes(hNodeType)
        ) {
            blocklyBlock
                .appendDummyInput('scanningOptions')
                .setAlign(Blockly.ALIGN_RIGHT)
                .appendField('  Display Scan Button')
                .appendField(new Blockly.FieldCheckbox(), 'displayScanButton')
                .appendField('  Display RFID Power Button')
                .appendField(new Blockly.FieldCheckbox(), 'displayRfidPowerButton')
                .appendField('  Scan RFID')
                .appendField(new Blockly.FieldCheckbox(), 'scanRfid')
                .appendField('  Scan Barcode')
                .appendField(new Blockly.FieldCheckbox(), 'scanBarcode');
        }
    }

    customizeToolbox(section) {
        section.contents.forEach(block => {
            // mutate the `block` as shown in the toolbox
            // but only if something is actually implemented for that block type (hNodeType)
            // block.type is something like 'searchElement_ValueSearch', so we only want the 2nd part
            const [, type] = block.type.split('_');
            if (this.types[type]?.customizeToolbox) {
                this.types[type]?.customizeToolbox?.(block);
            } else {
                if (block.type.includes('DropDown') || block.type.includes('MultiPickerDropDown')) {
                    block.inputs = {
                        filters: {
                            block: {
                                type: 'filter_OrderBy',
                                fields: { propertyName: 'title' }
                            }
                        }
                    };
                }
            }
        });
    }
}

module.exports = FormElement;

FormElement.adaptForPreview = function adaptForPreview(hNode) {
    hNode.treePosition = { sequence: 1, maxSequence: Number.MAX_SAFE_INTEGER, minSequence: 1, depth: 0 };
};
